import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { SplitPaneLayout } from '@/components/layout/SplitPaneLayout';
import { DetailDrawer } from '@/components/layout/DetailDrawer';
import { OpenHistoryTabs, type QueueTab } from '@/components/layout/OpenHistoryTabs';
import { QueueTable } from '@/components/tables/QueueTable';
import { QueueQuickFilters } from '@/components/tables/QueueQuickFilters';
import { EmptyState } from '@/components/tables/EmptyState';
import { ActionButton } from '@/components/ui/ActionButton';
import { apiClient } from '@/lib/api';
import { businessTodayParam } from '@/lib/businessDate';
import { getAutomation } from '@/automations';
import { QueueStatsStrip } from '@/components/layout/QueueStatsStrip';
import type {
  ApprovalRequest,
  AutomationQuickFilter,
  PaginatedResponse,
  QueueFacets,
} from '@/lib/types';

const LAPTOP_PX = 1024;
const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 200;

function useIsLaptopUp(): boolean {
  const [match, setMatch] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia(`(min-width: ${LAPTOP_PX}px)`).matches;
  });
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${LAPTOP_PX}px)`);
    const handler = (e: MediaQueryListEvent) => setMatch(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return match;
}

function parseTab(raw: string | null): QueueTab {
  return raw === 'history' ? 'history' : 'open';
}

function parsePositiveInt(raw: string | null, fallback: number): number {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? Math.min(n, MAX_LIMIT) : fallback;
}

function parseOffset(raw: string | null): number {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : 0;
}

function parseOrder(raw: string | null): 'asc' | 'desc' | null {
  return raw === 'asc' || raw === 'desc' ? raw : null;
}

function responseFacets<T>(data: PaginatedResponse<T> | T[] | undefined): QueueFacets | undefined {
  if (!data || Array.isArray(data)) return undefined;
  return data.facets;
}

function resolveQuickFilterValue(value: string): string {
  return value === '$today' ? businessTodayParam() : value;
}

export default function AutomationQueuePage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const config = getAutomation(slug);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));
  const isLaptopUp = useIsLaptopUp();
  const queryClient = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const limit = parsePositiveInt(searchParams.get('limit'), DEFAULT_LIMIT);
  const offset = parseOffset(searchParams.get('offset'));
  const currentPage = Math.floor(offset / limit) + 1;
  const sort = searchParams.get('sort');
  const order = parseOrder(searchParams.get('order'));
  const q = searchParams.get('q') ?? '';

  const activeFilterKeys = useMemo(
    () =>
      tab === 'history'
        ? config?.historyQueryParamKeys ?? []
        : config?.queueQueryParamKeys ?? [],
    [config?.historyQueryParamKeys, config?.queueQueryParamKeys, tab],
  );

  const activeQS = useMemo(() => {
    const next = new URLSearchParams();
    next.set('limit', String(limit));
    next.set('offset', String(offset));
    if (sort && order) {
      next.set('sort', sort);
      next.set('order', order);
    }
    if (q) next.set('q', q);
    for (const key of activeFilterKeys) {
      const v = searchParams.get(key);
      if (v) next.set(key, v);
    }
    return next.toString();
  }, [activeFilterKeys, limit, offset, order, q, searchParams, sort]);

  const queueQuery = useQuery<ApprovalRequest[] | PaginatedResponse<ApprovalRequest>>({
    queryKey: ['queue', slug, activeQS],
    queryFn: async () => {
      const res = await apiClient.get(`/automations/${slug}/queue?${activeQS}`);
      return res.data as ApprovalRequest[] | PaginatedResponse<ApprovalRequest>;
    },
    enabled: !!slug && !!config && tab === 'open',
  });

  const historyQuery = useQuery<ApprovalRequest[] | PaginatedResponse<ApprovalRequest>>({
    queryKey: ['history', slug, activeQS],
    queryFn: async () => {
      const url = `/automations/${slug}/history?${activeQS}`;
      const res = await apiClient.get(url);
      return res.data as ApprovalRequest[] | PaginatedResponse<ApprovalRequest>;
    },
    enabled: !!slug && !!config && tab === 'history',
  });

  const active = tab === 'history' ? historyQuery : queueQuery;
  const items = useMemo<ApprovalRequest[]>(
    () => (Array.isArray(active.data) ? active.data : active.data?.items ?? []),
    [active.data],
  );
  const total = Array.isArray(active.data) ? active.data.length : active.data?.total;
  const facets = responseFacets(active.data);

  const selectedRow = useMemo(
    () => items.find((r) => r.id === selectedId) ?? null,
    [items, selectedId],
  );

  if (!config) {
    return (
      <PageContainer>
        <EmptyState
          title="Unknown automation"
          description={`No automation registered with slug "${slug}".`}
        />
      </PageContainer>
    );
  }

  if (config.CustomQueuePage) {
    const Custom = config.CustomQueuePage;
    return <Custom />;
  }

  const columns =
    tab === 'history' ? config.historyColumns ?? config.queueColumns : config.queueColumns;

  const setTab = (next: QueueTab) => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (next === 'open') sp.delete('tab');
        else sp.set('tab', next);
        sp.delete('offset');
        sp.delete('sort');
        sp.delete('order');
        // Drop inactive automation-specific params when switching tabs.
        if (next === 'open') {
          for (const key of config.historyQueryParamKeys ?? []) {
            sp.delete(key);
          }
        } else {
          for (const key of config.queueQueryParamKeys ?? []) {
            sp.delete(key);
          }
        }
        return sp;
      },
      { replace: true },
    );
    setSelectedId(null);
    setDrawerOpen(false);
  };

  const onRefresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['queue', slug] });
    void queryClient.invalidateQueries({ queryKey: ['history', slug] });
    void queryClient.invalidateQueries({ queryKey: ['stats', slug] });
  };

  const onRowClick = (row: ApprovalRequest) => {
    // Seed from the row for immediate paint, then refresh detail in the background
    // so audit actions and any richer detail-only fields stay current.
    queryClient.setQueryData(['detail', slug, row.id], row);
    void queryClient.invalidateQueries({ queryKey: ['detail', slug, row.id] });
    setSelectedId(row.id);
    if (!isLaptopUp) setDrawerOpen(true);
  };

  const onDetailAction = () => {
    void queryClient.invalidateQueries({ queryKey: ['queue', slug] });
    void queryClient.invalidateQueries({ queryKey: ['history', slug] });
    void queryClient.invalidateQueries({ queryKey: ['stats', slug] });
  };

  const updateSearchParam = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (value) sp.set(key, value);
        else sp.delete(key);
        sp.delete('offset');
        return sp;
      },
      { replace: true },
    );
  };

  const setPage = (page: number) => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        sp.set('offset', String((page - 1) * limit));
        if (!sp.get('limit')) sp.set('limit', String(limit));
        return sp;
      },
      { replace: true },
    );
  };

  const setPageSize = (size: number) => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        sp.set('limit', String(size));
        sp.set('offset', '0');
        return sp;
      },
      { replace: true },
    );
  };

  const setSort = (nextSort: string | null, nextOrder: 'asc' | 'desc' | null) => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (nextSort && nextOrder) {
          sp.set('sort', nextSort);
          sp.set('order', nextOrder);
        } else {
          sp.delete('sort');
          sp.delete('order');
        }
        sp.set('offset', '0');
        return sp;
      },
      { replace: true },
    );
  };

  const applyQuickFilter = (filter: AutomationQuickFilter) => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        const active = Object.entries(filter.params).every(
          ([key, value]) => sp.get(key) === resolveQuickFilterValue(value),
        );
        for (const key of activeFilterKeys) {
          sp.delete(key);
        }
        if (!active) {
          for (const [key, value] of Object.entries(filter.params)) {
            sp.set(key, resolveQuickFilterValue(value));
          }
        }
        sp.set('offset', '0');
        return sp;
      },
      { replace: true },
    );
  };

  const Detail = config.DetailComponent;
  const HistoryFiltersComponent = config.HistoryFiltersComponent;
  const QueueFiltersComponent = config.QueueFiltersComponent;
  const QueueActionsComponent = config.QueueActionsComponent;
  const quickFilters = (config.quickFilters ?? []).filter((f) => !f.tab || f.tab === tab);
  const detailNode = selectedRow ? (
    <Detail request={selectedRow} onAction={onDetailAction} />
  ) : (
    <EmptyState
      title="Select a work item"
      description={`Pick a row from ${tab === 'history' ? 'history' : 'the queue'} to see details and actions.`}
    />
  );

  return (
    <PageContainer>
      <div className="flex flex-col gap-16">
        <header className="flex flex-col gap-12">
          <div className="flex flex-wrap items-start justify-between gap-12">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                {config.department}
              </p>
              <h1 className="text-3xl font-semibold text-neutral-900">{config.displayName}</h1>
            </div>
            <div className="flex items-center gap-12">
              <QueueStatsStrip slug={slug} />
              {QueueActionsComponent && (
                <QueueActionsComponent
                  tab={tab}
                  activeParams={searchParams}
                  facets={facets}
                  onComplete={onRefresh}
                  disabled={active.isFetching}
                />
              )}
              <ActionButton
                variant="secondary"
                size="sm"
                onClick={onRefresh}
                leadingIcon={<RefreshCw className="h-16 w-16" />}
                loading={active.isFetching}
              >
                Refresh
              </ActionButton>
            </div>
          </div>
          <OpenHistoryTabs value={tab} onChange={setTab} />
          <div className="flex flex-wrap items-end gap-12 rounded-lg border border-neutral-200 bg-neutral-0 p-12">
            <label className="flex min-w-[240px] flex-1 flex-col gap-6 text-sm font-medium text-neutral-700">
              <span>{tab === 'history' ? 'Search history' : 'Search queue'}</span>
              <input
                type="search"
                aria-label={tab === 'history' ? 'Search history' : 'Search queue'}
                value={q}
                onChange={(e) => updateSearchParam('q', e.target.value)}
                placeholder="Reference, vendor, invoice..."
                className="h-[40px] rounded-md border border-neutral-200 bg-neutral-0 px-10 text-sm font-normal text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </label>
            <QueueQuickFilters
              filters={quickFilters}
              activeParams={searchParams}
              facets={facets}
              onApply={applyQuickFilter}
            />
          </div>
          {tab === 'open' && QueueFiltersComponent && <QueueFiltersComponent />}
          {tab === 'history' && HistoryFiltersComponent && <HistoryFiltersComponent />}
        </header>

        {active.error ? (
          <EmptyState
            title={tab === 'history' ? "Couldn't load history" : "Couldn't load queue"}
            description={(active.error as Error).message ?? 'Try the refresh button.'}
          />
        ) : (
          <SplitPaneLayout
            left={
              <QueueTable<ApprovalRequest>
                columns={columns}
                data={items}
                total={total}
                loading={active.isLoading}
                getRowId={(r) => r.id}
                selectedRowId={selectedId}
                onRowClick={onRowClick}
                emptyState={config.emptyState}
                serverSidePagination={{
                  page: currentPage,
                  pageSize: limit,
                  onPageChange: setPage,
                  onPageSizeChange: setPageSize,
                }}
                serverSideSorting={{
                  sort: sort && order ? sort : null,
                  order: sort && order ? order : null,
                  onSortChange: setSort,
                }}
              />
            }
            right={detailNode}
          />
        )}
      </div>

      {!isLaptopUp && (
        <DetailDrawer
          open={drawerOpen && !!selectedRow}
          onClose={() => setDrawerOpen(false)}
          title={selectedRow ? `Request ${selectedRow.external_ref}` : 'Detail'}
        >
          {selectedRow && <Detail request={selectedRow} onAction={onDetailAction} />}
        </DetailDrawer>
      )}
    </PageContainer>
  );
}
